import {
  FileText,
  FolderOpen,
  HelpCircle,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Settings,
  ShieldCheck
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useOpenRun } from "../hooks/useOpenRun";
import { usePageMeta } from "../hooks/usePageMeta";
import { fetchHealth, fetchRuns } from "../lib/api";
import { apiUrl, googleAllowedDomain } from "../lib/env";
import { NAV_GROUPS, NO_RUN_OPEN, REFERENCE_ITEMS, WORK_ITEMS, type NavDestination } from "../lib/nav";
import { currentStage, runName, runStages } from "../lib/runFlow";
import { cacheRuns, loadCachedRuns, walkthroughStorageKey } from "../lib/storage";
import type { ActionState, AuthUser, McpCredential, RunStatus } from "../lib/types";
import { HelpPage } from "../screens/help/HelpPage";
import { RunsPage } from "../screens/library/RunsPage";
import { NewRunPage } from "../screens/new/NewRunPage";
import { PipelinePage } from "../screens/pipeline/PipelinePage";
import { SettingsPage } from "../screens/settings/SettingsPage";
import { StudioPage } from "../screens/studio/StudioPage";
import { WalkthroughOverlay } from "../screens/walkthrough/WalkthroughOverlay";
import { RunFlow } from "./RunFlow";
import { Button, DataList, NavItem, StatusPill } from "./primitives";
import { Sheet } from "./Sheet";

/**
 * THE APPLICATION CHROME — the frame all ten screens hang in.
 *
 * ================= THE NAV IS GROUPED BY KIND, BECAUSE ITS ITEMS ARE NOT PEERS ==========
 *
 * It used to be SEVEN FLAT PEERS that were four different kinds of thing:
 *
 *     New run   an ACTION            Pipeline  REFERENCE
 *     Library   a COLLECTION         Settings  CONFIG
 *     Editor    a PER-RUN STAGE      Help      REFERENCE
 *     Review    a PER-RUN STAGE
 *
 * Rendering them as one flat row of equal pills TELLS the operator they are equivalent
 * destinations, and they are not: two of them are meaningless without a run. The owner of
 * this product could not say what his own tabs pointed at. That is an information-
 * architecture failure, not a labelling one.
 *
 * And underneath it sat the specific bug that made the whole thing feel broken:
 *
 *     const latestRunId = runs[0]?.runId || "local";     // DELETED
 *     const editorPath  = `/runs/${latestRunId}/editor`; // DELETED
 *
 * "Editor" and "Review" linked at runs[0] — the most recent run — SILENTLY. They opened
 * some run without ever naming it, or saying that a run had been chosen at all.
 *
 * What replaces it:
 *   WORK       New run · Library                 — always here; they need no run.
 *   THIS RUN   only when a run is open, and it NAMES the run and shows the flow
 *              Draft -> Review -> Package with the stage the run is really at marked.
 *              No run open? It says so, and offers the two next steps. It never guesses.
 *   REFERENCE  Pipeline · Settings · Help        — how ATLAS is set up, what it checks.
 *
 * Every item carries ONE LINE saying what it actually shows (lib/nav.ts). Those lines are
 * painted in the menu sheet — which IS the nav on a phone — and in the header's caption
 * line at >=768 as the pointer or the keyboard crosses the row. They are never a tooltip
 * only a mouse can find.
 *
 * The stage a run is at is DERIVED, in lib/runFlow.ts, from the decisions the operator made
 * and the verdict the backend returned. Nothing in this chrome infers a QA pass, and the
 * Package step is "After QA passes" — not "ready" — until QA has actually passed.
 *
 * PHONE FIRST. The phone gets a 56px top bar carrying exactly three things (the mark, the
 * page title, one status chip) and a fixed bottom TAB BAR carrying five thumb-sized
 * destinations: New, Library, Draft, Review, and More — which opens the SAME grouped menu,
 * with every description, the named run and the same flow. The phone is not a degraded copy
 * of the desktop; it is the surface where the grouping is stated most completely.
 *
 * ACCESSIBILITY, WHICH WAS ABSENT:
 *   - A skip link, the first tabbable element in the document. There was none.
 *   - Route change moves focus to the <h1> and announces the title in a polite live region.
 *   - Every overlay is a Sheet, which is the modal contract: focus moved in, Tab trapped,
 *     Escape and backdrop and a visible 44px close button all dismiss, focus restored to the
 *     trigger. The app previously had no Escape handler anywhere at all.
 *
 * FROZEN: the Google Identity path is not touched from here. AuthGate owns it, and the
 * sign-in slot must keep a real, non-zero layout width at first paint.
 */

/** The glyph for each destination. The copy lives in lib/nav.ts; only the icon is here. */
const NAV_ICON: Record<string, React.ReactNode> = {
  new: <Plus />,
  library: <LayoutDashboard />,
  pipeline: <ShieldCheck />,
  settings: <Settings />,
  help: <HelpCircle />
};

type NavHint = { label: string; description: string } | null;

/** Redirect a legacy /editor or /review deep link into the Studio, preserving the run id. */
function RedirectToStudio() {
  const { runId } = useParams();
  return <Navigate to={`/runs/${runId}/studio`} replace />;
}

export function AppShell({
  user,
  onLogout,
  mcpCredential,
  mcpCredentialMessage,
  mcpCredentialState,
  refreshMcpCredential,
  removeMcpCredential
}: {
  user: AuthUser | null;
  onLogout: () => void;
  mcpCredential: McpCredential | null;
  mcpCredentialMessage: string;
  mcpCredentialState: ActionState;
  refreshMcpCredential: () => void;
  removeMcpCredential: () => void;
}) {
  const [runs, setRuns] = useState<RunStatus[]>(loadCachedRuns);
  /** The toast carries a nonce so the SAME message twice in a row still re-announces and
   *  still restarts its own dismissal timer. */
  const [toast, setToastState] = useState<{ text: string; id: number }>({ text: "", id: 0 });
  const [syncState, setSyncState] = useState<ActionState>("idle");
  const [statusOpen, setStatusOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  /** The description of the nav item the pointer or the keyboard is on. Null = say what the
   *  page you are ON shows, which is always true and never empty. */
  const [navHint, setNavHint] = useState<NavHint>(null);
  const [showWalkthrough, setShowWalkthrough] = useState(() => {
    try {
      return localStorage.getItem(walkthroughStorageKey) !== "1";
    } catch {
      return false;
    }
  });
  const [apiState, setApiState] = useState<"checking" | "connected" | "local">(
    apiUrl ? "checking" : "local"
  );

  const location = useLocation();
  const pageMeta = usePageMeta();
  const titleRef = useRef<HTMLHeadingElement>(null);
  /** The path we last moved focus FOR. Not a boolean "have I rendered": React 18 StrictMode
   *  mounts, unmounts and re-mounts every effect in development, so a first-render flag flips
   *  itself on the throwaway pass and the second pass then steals focus on FIRST PAINT — which
   *  would put a focus ring on the <h1> of a page nobody has navigated to yet, and drag focus
   *  off the walkthrough. Comparing the actual path is idempotent, so a repeated effect for the
   *  same route is a no-op. */
  const focusedPath = useRef<string | null>(null);

  const setToast = useCallback((text: string) => {
    setToastState((current) => ({ text, id: current.id + 1 }));
  }, []);

  useEffect(() => {
    if (!apiUrl) return;
    void refreshRuns({ silent: true });
    void checkApi({ silent: true });
  }, []);

  /**
   * ROUTE CHANGE: move focus to the page heading and close any open overlay.
   *
   * Without this a keyboard or screen-reader operator lands nowhere after a navigation —
   * focus stays on the link they just left, at the bottom of the tab bar, and nothing
   * announces that the page changed. Skipped on first paint so we never steal focus from a
   * deep link or from the walkthrough.
   */
  useEffect(() => {
    setMoreOpen(false);
    setStatusOpen(false);
    const previous = focusedPath.current;
    focusedPath.current = location.pathname;
    // Never on first paint, and never twice for the same route.
    if (previous === null || previous === location.pathname) return;
    titleRef.current?.focus();
  }, [location.pathname]);

  /** A toast reports a result; it does not become part of the furniture. */
  useEffect(() => {
    if (!toast.text) return;
    const timer = window.setTimeout(() => setToastState((current) => (current.id === toast.id ? { text: "", id: current.id } : current)), 6000);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.text]);

  async function checkApi(options: { silent?: boolean } = {}) {
    if (!options.silent) setToast("Checking API connection");
    setApiState(apiUrl ? "checking" : "local");
    try {
      const ok = await fetchHealth();
      const nextState = ok ? "connected" : "local";
      setApiState(nextState);
      if (!options.silent) setToast(ok ? "API connected" : "API did not respond");
      return nextState;
    } catch {
      setApiState("local");
      if (!options.silent) setToast("API did not respond");
      return "local" as const;
    }
  }

  async function refreshRuns(options: { silent?: boolean } = {}) {
    if (!apiUrl) {
      if (!options.silent) {
        setSyncState("error");
        setToast("Sync is unavailable in local preview");
      }
      return "local" as const;
    }
    if (!options.silent) {
      setSyncState("running");
      setToast("Syncing runs");
    }
    try {
      const nextRuns = await fetchRuns();
      setRuns(nextRuns);
      cacheRuns(nextRuns);
      if (!options.silent) {
        setSyncState("success");
        setToast("Runs synced");
      }
      return "success" as const;
    } catch {
      setApiState("local");
      if (!options.silent) {
        setSyncState("error");
        setToast("Sync failed");
      }
      return "error" as const;
    } finally {
      if (!options.silent) {
        window.setTimeout(() => setSyncState((current) => (current === "running" || current === "success" ? "idle" : current)), 2500);
      }
    }
  }

  function dismissWalkthrough() {
    try {
      localStorage.setItem(walkthroughStorageKey, "1");
    } catch {
      // The walkthrough is helpful but should never block use of the tool.
    }
    setShowWalkthrough(false);
  }

  function upsertRun(run: RunStatus) {
    setRuns((current) => {
      const exists = current.some((item) => item.runId === run.runId);
      const next = exists ? current.map((item) => (item.runId === run.runId ? run : item)) : [run, ...current];
      cacheRuns(next);
      return next;
    });
  }

  /**
   * WHICH RUN IS OPEN — and NEVER runs[0].
   *
   * The two lines this replaces are quoted at the top of the file. They pointed the Editor
   * and Review tabs at the most recent run without telling anyone. `openRun` is the run the
   * operator actually opened: the one in the URL, or the one they opened last and we
   * remembered. If neither exists, NOTHING is open, `stages` is null, and the chrome says
   * so — it does not pick a run on the operator's behalf.
   */
  const { run: openRun, onRunRoute } = useOpenRun(runs);
  const stages = openRun ? runStages(openRun) : null;
  const runStageHere = stages ? currentStage(stages) : null;

  const environmentLabel = apiState === "connected" ? "AWS" : "Local";
  const syncLabel =
    syncState === "running"
      ? "Syncing runs"
      : syncState === "success"
        ? "Runs synced"
        : syncState === "error"
          ? "Retry sync"
          : "Sync runs";

  /**
   * WHAT THE PAGE YOU ARE ON SHOWS. The caption line under the header nav defaults to this,
   * so it is never empty, never stale and never a guess — every route in the app resolves to
   * a destination or to a stage of the open run.
   */
  function activeHint(): NonNullable<NavHint> {
    const path = location.pathname;
    const stage = stages?.find((item) => item.to === path);
    if (stage) return { label: stage.label, description: stage.purpose };
    if (onRunRoute) return { label: NO_RUN_OPEN.label, description: NO_RUN_OPEN.description };
    const destination = [...WORK_ITEMS, ...REFERENCE_ITEMS].find((item) =>
      item.end ? path === item.to : path.startsWith(item.to)
    );
    if (destination) return { label: destination.label, description: destination.description };
    return { label: WORK_ITEMS[0].label, description: WORK_ITEMS[0].description };
  }

  const hint = navHint || activeHint();

  /** ONE destination, rendered the same way in the header row and in the menu sheet. */
  function destination(item: NavDestination, options: { hinting?: boolean } = {}) {
    return (
      <NavItem
        key={item.id}
        to={item.to}
        end={item.end}
        icon={NAV_ICON[item.id]}
        label={item.label}
        description={item.description}
        onHint={options.hinting ? setNavHint : undefined}
      />
    );
  }

  return (
    <div className="app-shell" data-testid="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <header className="app-header">
        <div className="app-header-inner">
          <Link className="app-mark" to="/new" aria-label="ATLAS Compiler by Meridian — new run">
            <img src="/meridian-logo.svg" alt="" />
            <span className="app-wordmark">
              ATLAS Compiler
              <small>by Meridian</small>
            </span>
          </Link>

          <h1 className="app-title" tabIndex={-1} ref={titleRef} data-testid="page-title">
            {pageMeta.title}
          </h1>

          {/* One control where there were three. It states the environment, and it opens the
              sheet that holds sync, the environment, the signed-in account and Logout. */}
          <button
            type="button"
            className="status-chip"
            data-testid="status-chip"
            data-sync={syncState}
            data-env={apiState}
            aria-haspopup="dialog"
            aria-expanded={statusOpen}
            onClick={() => setStatusOpen(true)}
          >
            {syncState === "running" ? (
              <span className="busy-spinner dark" aria-hidden="true" />
            ) : (
              <span className={`status-dot ${apiState}`} aria-hidden="true" />
            )}
            <span>{environmentLabel}</span>
          </button>

          {/* SIGNING OUT IS AN ACCOUNT ACT, SO IT LIVES ON THE ACCOUNT.
              It was reachable only by opening the ENVIRONMENT chip — a pill reading "AWS" — and
              scrolling a "Workspace status" sheet. Nobody looks for "sign out" under the name of
              their backend, and the operator who reported this concluded it had been removed. The
              account gets its own control, where every other product puts it. */}
          {user ? (
            <button
              type="button"
              className="account-chip"
              data-testid="account-chip"
              aria-haspopup="dialog"
              aria-expanded={accountOpen}
              aria-label={`Account — ${user.email}`}
              onClick={() => setAccountOpen(true)}
            >
              {user.picture ? (
                <img className="account-avatar" src={user.picture} alt="" />
              ) : (
                <span className="account-avatar account-initial" aria-hidden="true">
                  {(user.name || user.email).trim().charAt(0).toUpperCase()}
                </span>
              )}
              <span className="account-email">{user.email}</span>
            </button>
          ) : null}

          {/* THE NAV, GROUPED BY KIND. Three groups, each with its label painted, so the row
              can no longer present an action, a collection, a run stage and a reference page
              as four equivalent things. The groups are separated by a NEUTRAL hairline —
              structure, never a coloured edge. */}
          <nav className="app-nav" data-testid="nav" aria-label="Destinations">
            <div className="nav-group" data-group="work">
              <p className="nav-group-label" id="nav-group-work">
                {NAV_GROUPS.work.label}
              </p>
              <div className="nav-group-items" role="group" aria-labelledby="nav-group-work">
                {WORK_ITEMS.map((item) => destination(item, { hinting: true }))}
              </div>
            </div>

            {/* THIS RUN. It exists only when a run is open, and it NAMES the run — the whole
                point. The stages themselves are the flow bar under the header, where there is
                room to show all three with their real state; this chip is the way back into
                the run from anywhere else in the app. */}
            <div className="nav-group" data-group="run">
              <p className="nav-group-label" id="nav-group-run">
                {NAV_GROUPS.run.label}
              </p>
              <div className="nav-group-items" role="group" aria-labelledby="nav-group-run">
                {openRun && runStageHere ? (
                  <Link
                    className={`run-chip ${onRunRoute ? "active" : ""}`}
                    to={`/runs/${openRun.runId}/studio`}
                    data-testid="run-chip"
                    title={`${runName(openRun)} — open the ${runStageHere.label} stage`}
                    onMouseEnter={() =>
                      setNavHint({ label: runName(openRun), description: `${runStageHere.label} — ${runStageHere.purpose}` })
                    }
                    onMouseLeave={() => setNavHint(null)}
                    onFocus={() =>
                      setNavHint({ label: runName(openRun), description: `${runStageHere.label} — ${runStageHere.purpose}` })
                    }
                    onBlur={() => setNavHint(null)}
                  >
                    <span className="run-chip-name">{runName(openRun)}</span>
                    <StatusPill status={openRun.status} />
                  </Link>
                ) : (
                  /* NOT A LINK, because there is nowhere honest to send you. It states the
                     fact and the two real next steps sit beside it. */
                  <span className="run-chip run-chip-empty" data-testid="run-chip">
                    <FolderOpen aria-hidden="true" />
                    {NO_RUN_OPEN.label}
                  </span>
                )}
              </div>
            </div>

            <div className="nav-group" data-group="reference">
              <p className="nav-group-label" id="nav-group-reference">
                {NAV_GROUPS.reference.label}
              </p>
              <div className="nav-group-items" role="group" aria-labelledby="nav-group-reference">
                {REFERENCE_ITEMS.map((item) => destination(item, { hinting: true }))}
              </div>
            </div>
          </nav>

          {/* WHAT THIS ITEM SHOWS — one line, always populated, at >=768. It answers the
              owner's question ("what is this tab pointing to?") for the item under the
              pointer or the keyboard, and for the page you are on when neither is moving. */}
          <p className="nav-caption" data-testid="nav-caption">
            <strong>{hint.label}</strong>
            <span>{hint.description}</span>
          </p>
        </div>
      </header>

      <main className="route-frame" id="main-content">
        {/* THE RUN FLOW, ON THE RUN SCREENS, AT EVERY VIEWPORT. This is the surface that
            stops Editor and Review being anonymous: it names the run, states its status, and
            marks the stage the run is actually at. It renders only when the URL names a run
            AND we hold that run — if the id is unknown, the screen below says so and the
            chrome does not contradict it with a flow for a run it cannot show. */}
        {onRunRoute && openRun ? <RunFlow run={openRun} className="run-flow-bar" /> : null}
        <Routes>
          <Route path="/" element={<Navigate to="/new" replace />} />
          <Route path="/new" element={<NewRunPage onRun={upsertRun} setToast={setToast} />} />
          <Route path="/runs" element={<RunsPage runs={runs} />} />
          {/* The Studio is the run surface. The old /editor and /review routes redirect into it,
              so a bookmark or deep link can never land back on the retired screens. */}
          <Route path="/runs/:runId/editor" element={<RedirectToStudio />} />
          <Route path="/runs/:runId/review" element={<RedirectToStudio />} />
          <Route
            path="/runs/:runId/studio"
            element={<StudioPage runs={runs} onRun={upsertRun} setToast={setToast} />}
          />
          <Route path="/pipeline" element={<PipelinePage />} />
          <Route
            path="/settings"
            element={
              <SettingsPage
                apiState={apiState}
                checkApi={checkApi}
                mcpCredential={mcpCredential}
                mcpCredentialMessage={mcpCredentialMessage}
                mcpCredentialState={mcpCredentialState}
                refreshMcpCredential={refreshMcpCredential}
                removeMcpCredential={removeMcpCredential}
                setToast={setToast}
              />
            }
          />
          {/* Help READS the credential (it never mints, writes or displays the secret) and
              fires the ONE shared toast when the operator copies the endpoint or the client
              configuration. Both values already exist at shell level — Settings takes the
              same credential — so this adds no capability, no API call and no new state. */}
          <Route path="/help" element={<HelpPage mcpCredential={mcpCredential} setToast={setToast} />} />
        </Routes>
      </main>

      {/* THE THUMB ZONE. Five destinations is the Apple/Fluent ceiling: the two WORK
          destinations, the two run STAGES, and More — which opens the grouped menu holding
          every description, the named run, the full flow and the three REFERENCE surfaces.

          DRAFT AND REVIEW ARE RUN-SCOPED, AND THE BAR IS HONEST ABOUT IT. With a run open
          they are links to that run's stages. With NO run open there is nothing to link to,
          so they do not silently open the newest run (which is exactly what they used to
          do): they open the menu, whose "This run" group says no run is open and offers the
          two real next steps. The bar never changes shape, and it never lies. */}
      <nav className="tab-bar" data-testid="tab-bar" aria-label="Primary">
        <NavItem to="/new" icon={<Plus />} label="New" end />
        <NavItem to="/runs" icon={<LayoutDashboard />} label="Library" end />
        {stages && openRun ? (
          <>
            <NavItem to={`/runs/${openRun.runId}/studio`} icon={<FileText />} label="Studio" />
          </>
        ) : (
          <>
            <NoRunTab label="Draft" icon={<FileText />} onOpen={() => setMoreOpen(true)} />
            <NoRunTab label="Review" icon={<ListChecks />} onOpen={() => setMoreOpen(true)} />
          </>
        )}
        <button
          type="button"
          className={`nav-item ${moreOpen ? "active" : ""}`}
          data-testid="nav-link"
          data-nav="More"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
        >
          <span className="nav-item-glyph" aria-hidden="true">
            <MoreHorizontal />
          </span>
          <span className="nav-item-label" data-testid="nav-label" data-active={moreOpen ? "true" : "false"}>
            More
          </span>
        </button>
      </nav>

      {/* Both the accessibility contract and the smoke gate's success criterion: a VISIBLE
          text node with role="status". It floats above the tab bar and cannot intercept a
          tap (pointer-events: none). */}
      {toast.text ? (
        <div className="toast" role="status" aria-live="polite" data-testid="toast">
          {toast.text}
        </div>
      ) : null}

      {/* The new page title, spoken. The <h1> shows it; this says it. */}
      <p className="sr-only" role="status" aria-live="polite" data-testid="route-announcer">
        {pageMeta.title}
      </p>

      {statusOpen ? (
        <Sheet title="Workspace status" testId="status-sheet" onClose={() => setStatusOpen(false)}>
          <DataList
            items={[
              ["Environment", apiState === "connected" ? "AWS backend" : "Local preview"],
              ["Runs on this device", String(runs.length)],
              ["Signed in", user ? user.email : "Local preview — no account"]
            ]}
          />
          <div className="status-sheet-actions">
            <Button
              variant="secondary"
              block
              busy={syncState === "running"}
              disabled={syncState === "running"}
              data-testid="sync-button"
              data-state={syncState}
              onClick={() => {
                setStatusOpen(false);
                void refreshRuns();
              }}
            >
              {syncState === "running" ? null : <RefreshCw aria-hidden="true" />}
              {syncLabel}
            </Button>
          </div>
        </Sheet>
      ) : null}

      {/* Sign-out has exactly one home, and it is here. */}
      {accountOpen && user ? (
        <Sheet title="Account" testId="account-sheet" onClose={() => setAccountOpen(false)}>
          <DataList
            items={[
              ["Signed in", user.email],
              ["Access", `Limited to ${googleAllowedDomain}`]
            ]}
          />
          <div className="status-sheet-actions">
            <Button
              variant="danger"
              block
              data-testid="logout"
              onClick={() => {
                setAccountOpen(false);
                onLogout();
              }}
            >
              <LogOut aria-hidden="true" />
              Sign out
            </Button>
          </div>
        </Sheet>
      ) : null}

      {/* THE MENU — the whole nav, grouped, with every description painted. On a phone this
          IS the navigation, so it is not a leftovers drawer: it carries the same three groups
          the desktop header carries, in the same order, with the same words, plus the run's
          identity and the same three-stage flow. The phone is not a degraded copy. */}
      {moreOpen ? (
        <Sheet title="Where everything lives" testId="more-sheet" onClose={() => setMoreOpen(false)}>
          <div className="sheet-menu">
            <section className="sheet-group">
              <h3 className="sheet-group-label">{NAV_GROUPS.work.label}</h3>
              <p className="sheet-group-meaning">{NAV_GROUPS.work.meaning}</p>
              <nav className="sheet-nav" aria-label={NAV_GROUPS.work.label}>
                {WORK_ITEMS.map((item) => destination(item))}
              </nav>
            </section>

            <section className="sheet-group">
              <h3 className="sheet-group-label">{NAV_GROUPS.run.label}</h3>
              {openRun ? (
                <RunFlow run={openRun} className="run-flow-sheet" />
              ) : (
                <div className="sheet-no-run" data-testid="sheet-no-run">
                  <p className="sheet-no-run-title">
                    <FolderOpen aria-hidden="true" />
                    {NO_RUN_OPEN.label}
                  </p>
                  <p className="sheet-no-run-body">{NO_RUN_OPEN.description}</p>
                  <div className="sheet-no-run-actions">
                    <Link className="btn btn-primary btn-md btn-block" to="/new" data-testid="sheet-start-run">
                      <Plus aria-hidden="true" />
                      Start a run
                    </Link>
                    <Link className="btn btn-secondary btn-md btn-block" to="/runs" data-testid="sheet-open-library">
                      <LayoutDashboard aria-hidden="true" />
                      Open the Library
                    </Link>
                  </div>
                </div>
              )}
            </section>

            <section className="sheet-group">
              <h3 className="sheet-group-label">{NAV_GROUPS.reference.label}</h3>
              <p className="sheet-group-meaning">{NAV_GROUPS.reference.meaning}</p>
              <nav className="sheet-nav" aria-label={NAV_GROUPS.reference.label}>
                {REFERENCE_ITEMS.map((item) => destination(item))}
              </nav>
            </section>
          </div>
        </Sheet>
      ) : null}

      {showWalkthrough ? <WalkthroughOverlay onClose={dismissWalkthrough} /> : null}
    </div>
  );
}

/**
 * A RUN STAGE TAB WITH NO RUN BEHIND IT.
 *
 * This is the honest replacement for the tab that used to link at `/runs/${runs[0]}/editor`.
 * There is no run to open, so there is no destination — and inventing one (the newest run,
 * a redirect to Library, a route with "local" in it) is the exact dishonesty being deleted.
 * It keeps the tab, keeps its 44px target and keeps the bar at five items; tapping it opens
 * the menu, where the "This run" group says no run is open and offers the two next steps.
 * The label stays the stage's own name, so the bar never changes shape between states.
 */
function NoRunTab({ label, icon, onOpen }: { label: string; icon: React.ReactNode; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="nav-item nav-item-norun"
      data-testid="nav-link"
      data-nav={label}
      data-norun="true"
      aria-haspopup="dialog"
      aria-label={`${label} — no run is open`}
      onClick={onOpen}
    >
      <span className="nav-item-glyph" aria-hidden="true">
        {icon}
      </span>
      <span className="nav-item-label" data-testid="nav-label" data-active="false">
        {label}
      </span>
    </button>
  );
}
